<template>
  <v-menu
    v-if="!readonly"
    offset-y
    content-class="w-tree-selection-filter"
    fixed
    max-height="60vh"
    :close-on-content-click="!multiple"
  >
    <template #activator="{ on: { click } }">
      <component
        :is="dynamicComponent"
        v-bind="filterProp"
        :value="computedValue"
        item-value="composedUnique"
        :items="flatItems"
        :menu-props="{
          openOnClick: openDefaultMenu,
          closeOnClick: true,
          value: menuProp,
          offsetY: true,
        }"
        :cache-items="false"
        :sort-items="false"
        @update:search-input="(event) => (search = event)"
        @click:clear="handleClear"
        @change="invokeInputByAutocomplete"
        @click.native="click"
      />
    </template>
    <w-treeview
      v-if="flatItems.length > 0"
      ref="treeview"
      :value="[value]"
      :items="mappedStoredItems"
      :item-text="itemText"
      :item-children="itemChildren"
      item-key="uuid"
      :search="search"
      :return-object="returnObject"
    >
      <template #prepend="{ item, open }">
        <section class="d-flex">
          <w-checkbox
            v-if="multiple && item.resource == WORKFLOW_TYPES.ACTIONS"
            :value="isItemSelected(item.id)"
            @change="invokeSelectItem(item)"
          />
          <w-icon v-if="item.resource == WORKFLOW_TYPES.WORKFLOWS">
            {{ open ? 'mdi-folder-open' : 'mdi-folder' }}
          </w-icon>
          <w-icon
            v-else-if="item.resource == WORKFLOW_TYPES.ACTIONS"
            @click="invokeSelectItem(item)"
          >
            $wDocument
          </w-icon>
        </section>
      </template>
      <template #label="{ item }">
        <div
          v-if="item.resource == WORKFLOW_TYPES.ACTIONS"
          style="cursor: pointer"
          @click="invokeSelectItem(item)"
        >
          {{ item.data.label }}
        </div>
        <span
          v-else
          @click.stop="$event.stopPropagation()"
        >{{ item.data.label }}
        </span>
      </template>
    </w-treeview>
  </v-menu>
  <w-read-only-text-field
    v-else
    :label="label"
    :value="getItemText"
  />
</template>

<script>
import workflowTreeSelectMixin from '@/components/base_components/workflow/workflow_inputs/workflow_action_picker/mixins/workflow-tree-select-mixin';
import WorkAutocomplete from '@/components/global_components/forms/WorkAutocomplete';
import { filterProps } from '@/utils/functional-component-utils';

import { addUUIDs } from '@/utils/uuid-utils';
import WORKFLOW_TYPES from './WORKFLOW_TYPES_ENUM';

import get from 'lodash/get';

const _byId = (value) => (item) => item.resource == WORKFLOW_TYPES.ACTIONS && item.id == value

export default {
  name: 'WorkflowActionSelect',
  mixins: [
    workflowTreeSelectMixin
  ],
  props: {
    ...WorkAutocomplete.props,
    itemChildren: { type: String, default: 'data.actions.data' },
    rules: { type: [Array, String] },
    initialValue: { type: Object },
    withFormField: { type: Boolean, default: true },
  },
  data() {
    return {
      internalMultipleValue: [] // without this the value-prop does not update properly for multiple select while using this component without a GenericFilter
    }
  },
  computed: {
    getItemText() {
      return get(this.computedValue, this.itemText);
    },
    mappedStoredItems() {
      return addUUIDs(this.items);
    },
    filterProp() {
      return filterProps(this.$props, ['items', 'value']);
    },
    dynamicComponent() {
      return this.withFormField ? 'w-form-autocomplete' : 'w-autocomplete';
    },
    computedValue() {
        if (this.multiple)
          return this.internalMultipleValue ? this.internalMultipleValue.map((val) => this.flatItems.find( _byId(val))) : []

        if (this.returnObject) 
          return this.value;
        
        return this.flatItems.find(_byId(this.value));
    },
  },
  created() {
    if (this.initialValue) this.setInitialValue();
  },
  methods: {
    invokeSelectItem(event) { 
      if (!event) {
        if (this.multiple)
          this.internalMultipleValue = []
        this.$emit('input', this.multiple ? this.internalMultipleValue : undefined);
        return;
      }
      let item = this.flatItems.find(
        (item) => item.resource == event.resource && item.id == event.id
      );

      if (this.multiple) {
        let itemId = item?.id;
        if (this.internalMultipleValue != null && this.internalMultipleValue?.includes(itemId)) {
          this.internalMultipleValue = this.internalMultipleValue.filter((item) => item != itemId);
          this.$emit(
            'input',
            this.internalMultipleValue
          );
        } else {
          let val = this.internalMultipleValue ?? [];
          this.internalMultipleValue = [...val, get(item, this.itemValue)];
          this.$emit('input', this.internalMultipleValue);
        }
      } else {
        if (!this.returnObject) this.$emit('input', get(item, this.itemValue));
        else this.$emit('input', item);
      }
    },
    invokeInputByAutocomplete(event) {
      if (event?.length == 0) this.invokeSelectItem(undefined)
      if (this.multiple && event?.length != 0) {
        let diff = this._findDiff(event);
        this.invokeSelectItem(diff);
      }
    },
    handleClear() {
      if (this. multiple)
        this.internalMultipleValue = []
      this.$emit('input', this.multiple ? this.internalMultipleValue : undefined);
    },
    setInitialValue() {
      if (this.initialValue) {
        let item = this._findItemByAction(
          this.initialValue.action ?? this.initialValue.id
        );

        if (item) this.invokeSelectItem(item);
      }
    },
    isItemSelected(itemId) {
      return this.value?.includes(itemId);
    },
    /**
     * Helpers
     */
    _findDiff(event) {
      let items = this.computedValue
        .map((item) => item.composedUnique)
        .filter((x) => event.indexOf(x) === -1);
      return this.computedValue.find(val => val.composedUnique == items[0])
    },
    _findItemByAction(action) {
      return this.flatItems.find(
        (flatItem) =>
          flatItem.id == action &&
          flatItem.resource == WORKFLOW_TYPES.ACTIONS
      );
    },
  },
  watch: {
    value: {
      handler() {
        if (this.multiple) {
          this.internalMultipleValue = this.value;
        }
      }, immediate: true,
    },
    flatItems: {
      handler() {
        if(this.initialValue){
          this.setInitialValue();
        }
      }
    }
  }
};
</script>
<style lang="scss" scoped>
::v-deep .w-treeview {
  .v-treeview-node--leaf > .v-treeview-node__root {
    padding-left: 0.5rem !important;
  }
  .v-treeview-node {
    .v-treeview-node__prepend {
      min-width: 0;
    }
    .v-treeview-node__root {
      padding-left: 0;
    }
  }
}
</style>
